import type { AdditionalFields } from '@better-auth-ui/core'
import type { Messages } from '~/lib/i18n/messages'
import type { Locale } from '~/lib/i18n/shared'
import { AuthProvider } from '@better-auth-ui/heroui'
import { ToastProvider } from '@heroui/react'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'

import { useTheme } from 'next-themes'
import * as React from 'react'
import { useLocale, useMessages } from 'use-intl'
import { invalidateBrowserAuthenticationState } from '~/lib/api-client'
import { resolveAuthRedirectTo } from '~/lib/auth/redirect'
import { getAuthBasePath } from '~/lib/auth/routes'
import { getBetterAuthLocalization } from '~/lib/i18n/auth-localization'
import { SESSION_QUERY_KEY } from '~/lib/session-query'
import { authClient } from '~/utils/auth'

interface AuthIdentity {
  session?: { id?: unknown } | null
  user?: { id?: unknown } | null
}
interface ExpectedAuthTransition {
  from: string | null
  to: string | null
}

function authIdentity(value: AuthIdentity | null | undefined): string | null {
  if (typeof value?.session?.id === 'string') {
    return `session:${value.session.id}`
  }
  if (typeof value?.user?.id === 'string') {
    return `user:${value.user.id}`
  }
  return null
}

export function LocalizedAuthProvider({ children }: { children: React.ReactNode }) {
  const navigateFn = useNavigate()
  const queryClient = useQueryClient()
  const expectedAuthTransitionRef = React.useRef<ExpectedAuthTransition | null>(null)
  const observedAuthIdentityRef = React.useRef<string | null>(null)
  const authNavigationSequenceRef = React.useRef(0)
  const authStateGenerationRef = React.useRef(0)
  const location = useLocation()
  const { theme, setTheme } = useTheme()
  const locale = useLocale() as Locale
  const messages = useMessages() as Messages
  const authBasePath = getAuthBasePath(locale)

  const { data, isPending } = authClient.useSession()
  React.useEffect(() => {
    if (isPending || data === undefined) {
      return
    }
    const observedIdentity = authIdentity(data)
    observedAuthIdentityRef.current = observedIdentity
    let unexpectedTransition = false
    const expectedTransition = expectedAuthTransitionRef.current
    if (expectedTransition) {
      if (observedIdentity === expectedTransition.from) {
        return
      }
      expectedAuthTransitionRef.current = null
      if (observedIdentity === expectedTransition.to) {
        return
      }
      unexpectedTransition = true
    }
    const cachedSession = queryClient.getQueryData(SESSION_QUERY_KEY) as AuthIdentity | undefined
    if (unexpectedTransition || observedIdentity !== authIdentity(cachedSession)) {
      authStateGenerationRef.current += 1
      invalidateBrowserAuthenticationState()
    }
  }, [data, isPending, queryClient])

  React.useEffect(() => {
    if (data?.user) {
      Sentry.setUser({ id: data.user.id })
    }
    else {
      Sentry.setUser(null)
    }
  }, [data?.user])

  // Extract auth messages from use-intl configuration
  const localization = React.useMemo(() => {
    try {
      const loc = getBetterAuthLocalization(messages, locale)
      return loc
    }
    catch (error) {
      Sentry.captureException(error, { extra: { source: 'auth_localization' } })
      return undefined
    }
  }, [messages, locale])

  const complianceUi = React.useMemo(() => locale === 'zh-CN'
    ? {
        termsLabel: '我已阅读并接受服务条款、隐私政策和免责声明',
        termsRequired: '必须接受服务条款、隐私政策和免责声明',
        ageLabel: '我确认已年满 18 周岁',
        ageRequired: '必须确认已年满 18 周岁',
      }
    : {
        termsLabel: 'I accept the Terms of Service, Privacy Policy, and Disclaimer',
        termsRequired: 'You must accept the Terms, Privacy Policy, and Disclaimer',
        ageLabel: 'I confirm that I am at least 18 years old',
        ageRequired: 'You must confirm that you are at least 18 years old',
      }, [locale])

  const complianceFields = React.useMemo<AdditionalFields>(() => [
    {
      name: 'termsAccepted',
      type: 'boolean',
      inputType: 'checkbox',
      label: complianceUi.termsLabel,
      required: true,
      signUp: 'below',
      profile: false,
      validate: (value) => {
        if (value !== true) {
          throw new Error(complianceUi.termsRequired)
        }
      },
    },
    {
      name: 'ageConfirmed',
      type: 'boolean',
      inputType: 'checkbox',
      label: complianceUi.ageLabel,
      required: true,
      signUp: 'below',
      profile: false,
      validate: (value) => {
        if (value !== true) {
          throw new Error(complianceUi.ageRequired)
        }
      },
    },
  ], [complianceUi])

  // Adapter for navigate to match expected signature
  const handleNavigate = React.useCallback(async (options: { to: string, replace?: boolean }) => {
    const previousIdentity = authIdentity(data)
    const navigationSequence = authNavigationSequenceRef.current + 1
    const authStateGeneration = authStateGenerationRef.current
    authNavigationSequenceRef.current = navigationSequence
    invalidateBrowserAuthenticationState()
    const { data: currentSession, error } = await authClient.getSession()
    if (navigationSequence !== authNavigationSequenceRef.current) {
      return
    }
    const currentIdentity = authIdentity(currentSession)
    const observedIdentity = observedAuthIdentityRef.current
    const canSeedCurrentSession = authStateGeneration === authStateGenerationRef.current
      && (observedIdentity === previousIdentity || observedIdentity === currentIdentity)
    if (!error && canSeedCurrentSession) {
      expectedAuthTransitionRef.current = previousIdentity === currentIdentity
        ? null
        : { from: previousIdentity, to: currentIdentity }
      queryClient.setQueryData(SESSION_QUERY_KEY, {
        session: currentSession?.session ?? null,
        user: currentSession?.user ?? null,
      })
    }
    else {
      expectedAuthTransitionRef.current = null
    }
    navigateFn({ to: options.to as any, replace: options.replace })
  }, [data, navigateFn, queryClient])
  const redirectTo = React.useMemo(() => {
    return resolveAuthRedirectTo(location as any)
  }, [location])

  return (
    <AuthProvider
      authClient={authClient as any}
      // @ts-expect-error type mismatch with better-auth-ui
      appearance={{ theme: theme as any, setTheme }}
      localization={localization as any}
      additionalFields={complianceFields}
      redirectTo={redirectTo}
      navigate={handleNavigate as any}
      basePaths={{ auth: authBasePath, settings: '/settings', organization: '/organization' }}
      viewPaths={{
        SIGN_IN: 'sign-in',
        SIGN_UP: 'sign-up',
        FORGOT_PASSWORD: 'forgot-password',
        RESET_PASSWORD: 'reset-password',
      } as any}
    >
      {children}
      <ToastProviderAdapter />
    </AuthProvider>
  )
}

function ToastProviderAdapter() {
  return <ToastProvider />
}
